import i18n, { TFunction } from 'i18next';
import {
  getCountryLabelFromCountries,
  localizeCountryInCompositeLabel,
} from 'i18n/countryLabel';
import moment from 'moment';
import { Provider } from 'store/api/enums';
import { EntityStatus } from 'store/app/enums';
import { Country } from 'store/common/models';
import {
  Mapping,
  Organisation,
  ReceiptEntityStatistics,
} from 'store/organisation/models';
import { isReceiptsEntityProvider } from 'utils/entity-capture-column';
import { getProviderName } from 'utils/get-provider-name';
import { getReceiptEntityStatsFromMap } from 'utils/receipt-statistics';

const formatCaptureStat = (
  stats: ReceiptEntityStatistics | undefined,
  value: number | undefined
): string => (stats != null && value != null ? String(value) : '');

const LIST_SEPARATOR = '; ';

const formatDate = (value: string | number | null | undefined): string => {
  if (value == null || value === '') return '';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
};

const formatBoolean = (value: boolean | undefined, t: TFunction): string => {
  if (value === undefined) return '';
  return value ? t('common.yes') : t('common.no');
};

const joinList = (items: (string | null | undefined)[]): string =>
  items
    .filter((item) => item != null && String(item).trim() !== '')
    .join(LIST_SEPARATOR);

const getEntityStatusLabel = (org: Organisation, t: TFunction): string => {
  if (org.status === EntityStatus.VIEW_ONLY) {
    return t('organisation.export.status-view-only');
  }
  return t('common.active');
};

const getConnectionHealthLabel = (org: Organisation, t: TFunction): string => {
  if (org.status === EntityStatus.VIEW_ONLY) {
    return t('organisation.export.status-view-only');
  }
  if (
    org.isGroup ||
    org.providerName?.toLowerCase() === Provider.GROUP.toLowerCase()
  ) {
    return t('main.connected');
  }
  if (org.connectedProperly) {
    return t('main.connected');
  }
  if (org.providerName?.toLowerCase() === Provider.EXCEL.toLowerCase()) {
    return getEntityStatusLabel(org, t);
  }
  return t('organisation.export.connection-reconnect-required');
};

const getMappingDisplayName = (
  org: Organisation,
  mapping: Mapping,
  countries: Country[]
): string => {
  const returnType = org.returnTypesEdit?.find(
    (rt) => rt.id === mapping.returnTypeId
  );
  const countryName = returnType?.countryName;
  return countryName
    ? `${getCountryLabelFromCountries(
        countryName,
        countries,
        i18n.language,
        countryName
      )} - ${mapping.mappingName}`
    : mapping.mappingName;
};

const getOrgMappings = (
  org: Organisation,
  mappings: Mapping[] | undefined
): Mapping[] =>
  mappings?.filter((mapping) => mapping.organisationId === org.uuid) ?? [];

const getGroupedConnectionMeta = (org: Organisation) => {
  const match = org.groupedConnections?.find(
    (connection) =>
      connection.connectionId === org.connectionId ||
      connection.uuid === org.uuid
  );
  return match ?? org.groupedConnections?.[0];
};

export async function exportEntitiesToExcel(
  organisations: Organisation[],
  countries: Country[],
  mappings: Mapping[] | undefined,
  t: TFunction,
  statsByConnectionId?: Map<string, ReceiptEntityStatistics>
): Promise<void> {
  const XLSX = await import('xlsx');

  const columns = {
    nameOfCompany: t('organisation.export.column-name-of-company'),
    legalName: t('organisation.export.column-legal-name'),
    alternateName: t('organisation.alternate-name'),
    country: t('common.country'),
    connectionType: t('organisation.export.column-connection-type'),
    providerShortCode: t('organisation.export.column-provider-short-code'),
    internalCode: t('organisation.code'),
    taxNumber: t('organisation.export.column-tax-number'),
    currency: t('common.currency'),
    firstConnectionDate: t('organisation.export.column-first-connection-date'),
    createdBy: t('organisation.created-by'),
    status: t('main.status'),
    connectionHealth: t('organisation.connection-status'),
    taxEnabled: t('organisation.export.column-tax-enabled'),
    vatCheckerActive: t('organisation.export.column-vat-checker-active'),
    taxRatesConfigured: t('organisation.export.column-tax-rates-configured'),
    mappedReturns: t('organisation.export.column-mapped-returns'),
    returnFrequencies: t('organisation.export.column-return-frequencies'),
    nextDueDates: t('organisation.export.column-next-due-dates'),
    nextPeriods: t('organisation.export.column-next-periods'),
    cashAccounting: t('organisation.cash-accounting'),
    assignedUsers: t('organisation.export.column-assigned-users'),
    assignedUserEmails: t('organisation.export.column-assigned-user-emails'),
    contactPersonName: t('organisation.contact-person-name'),
    contactPersonEmail: t('organisation.contact-person-email'),
    contactPersonTelephone: t('organisation.contact-person-telephone'),
    registeredAddress: t('organisation.registered-address'),
    notes: t('organisation.notes'),
    totalTaxBalance: t('organisation.export.column-total-tax-balance'),
    isGroup: t('organisation.export.column-is-group'),
    groupMembers: t('organisation.export.column-group-members'),
    receiptsCaptureEmail: t(
      'organisation.export.column-receipts-capture-email'
    ),
    capturePendingReview: t(
      'organisation.export.column-capture-pending-review'
    ),
    capturePendingReviewTransactions: t(
      'organisation.export.column-capture-pending-review-transactions'
    ),
    captureReviewed: t('organisation.export.column-capture-reviewed'),
    captureReviewedTransactions: t(
      'organisation.export.column-capture-reviewed-transactions'
    ),
    capturePostedFiles: t('organisation.export.column-capture-posted-files'),
    capturePostedThisMonth: t(
      'organisation.export.column-capture-posted-this-month'
    ),
    captureUploadedTotal: t(
      'organisation.export.column-capture-uploaded-total'
    ),
    captureUploadedThisMonth: t(
      'organisation.export.column-capture-uploaded-this-month'
    ),
    captureUploadedManually: t(
      'organisation.export.column-capture-uploaded-manually'
    ),
    captureUploadedFromEmail: t(
      'organisation.export.column-capture-uploaded-from-email'
    ),
    captureUploadedFromGoogleDrive: t(
      'organisation.export.column-capture-uploaded-from-google-drive'
    ),
    captureUploadedFromOnedrive: t(
      'organisation.export.column-capture-uploaded-from-onedrive'
    ),
    captureFilesWithErrors: t(
      'organisation.export.column-capture-files-with-errors'
    ),
    captureDeletedFiles: t('organisation.export.column-capture-deleted-files'),
    captureActiveFiles: t('organisation.export.column-capture-active-files'),
    hmrcConnectionLinked: t(
      'organisation.export.column-hmrc-connection-linked'
    ),
    organisationId: t('organisation.export.column-organisation-id'),
    connectionId: t('organisation.export.column-connection-id'),
    version: t('organisation.export.column-version'),
    lastUsed: t('organisation.export.column-last-used'),
    lastValidated: t('organisation.export.column-last-validated'),
    clientCode: t('organisation.export.column-client-code'),
    officeLocation: t('organisation.export.column-office-location'),
    demoCompany: t('organisation.export.column-demo-company'),
  };

  const exportRows = organisations
    .filter(
      (org) => org.providerName?.toLowerCase() !== Provider.CIN7.toLowerCase()
    )
    .map((org) => {
      const countryName = getCountryLabelFromCountries(
        org.country,
        countries,
        i18n.language,
        countries.find((country) => country.iso === org.country)?.name ||
          org.country ||
          ''
      );
      const orgMappings = getOrgMappings(org, mappings);
      const details = org.organisationDetails;
      const connectionMeta = getGroupedConnectionMeta(org);
      const isGroup =
        org.isGroup ||
        org.providerName?.toLowerCase() === Provider.GROUP.toLowerCase();
      const isReceiptsEntity = isReceiptsEntityProvider(org.providerName);
      const captureStats = getReceiptEntityStatsFromMap(
        statsByConnectionId ?? new Map(),
        { connectionId: org.connectionId, organisationId: org.uuid }
      );
      const includeReadyToPostStats = !isReceiptsEntity;

      return {
        [columns.nameOfCompany]: org.friendlyName || '',
        [columns.legalName]: org.legalName || '',
        [columns.alternateName]: details?.alternateName || '',
        [columns.country]: countryName,
        [columns.connectionType]: getProviderName(org.providerName),
        [columns.providerShortCode]: org.shortCode || '',
        [columns.internalCode]: details?.code || '',
        [columns.taxNumber]: org.taxNumber || '',
        [columns.currency]: org.currency || '',
        [columns.firstConnectionDate]: formatDate(org.createTimestamp),
        [columns.createdBy]: org.creator || '',
        [columns.status]: getEntityStatusLabel(org, t),
        [columns.connectionHealth]: getConnectionHealthLabel(org, t),
        [columns.taxEnabled]: formatBoolean(org.taxEnabled, t),
        [columns.vatCheckerActive]: formatBoolean(org.vatCheckerActive, t),
        [columns.taxRatesConfigured]: formatBoolean(org.hasTaxRates, t),
        [columns.mappedReturns]: joinList(
          orgMappings.map((mapping) =>
            getMappingDisplayName(org, mapping, countries)
          )
        ),
        [columns.returnFrequencies]: joinList(
          orgMappings.map((mapping) =>
            mapping.returnFrequency != null
              ? String(mapping.returnFrequency)
              : ''
          )
        ),
        [columns.nextDueDates]: joinList(
          orgMappings.map((mapping) => formatDate(mapping.returnDueDate))
        ),
        [columns.nextPeriods]: joinList(
          orgMappings.map((mapping) => mapping.nextPeriod || '')
        ),
        [columns.cashAccounting]: joinList(
          orgMappings.map((mapping) => formatBoolean(mapping.cashAccounting, t))
        ),
        [columns.assignedUsers]: joinList(
          org.users?.map(
            (user) =>
              user.fullName ||
              [user.firstName, user.lastName].filter(Boolean).join(' ')
          )
        ),
        [columns.assignedUserEmails]: joinList(
          org.users?.map((user) => user.email)
        ),
        [columns.contactPersonName]: details?.contactPersonName || '',
        [columns.contactPersonEmail]: details?.contactPersonEmail || '',
        [columns.contactPersonTelephone]: details?.contactPersonTelephone || '',
        [columns.registeredAddress]: details?.registeredAddress || '',
        [columns.notes]: details?.notes || '',
        [columns.totalTaxBalance]:
          details?.totalTaxBalance != null
            ? String(details.totalTaxBalance)
            : '',
        [columns.isGroup]: formatBoolean(isGroup, t),
        [columns.groupMembers]: joinList(
          org.groupedConnections?.map(
            (connection) => connection.name || connection.legalName
          )
        ),
        [columns.receiptsCaptureEmail]: org.scanReceiptEmail || '',
        [columns.capturePendingReview]: formatCaptureStat(
          captureStats,
          captureStats?.filesPendingReview
        ),
        [columns.capturePendingReviewTransactions]: formatCaptureStat(
          captureStats,
          captureStats?.transactionsPendingReview
        ),
        [columns.captureReviewed]: includeReadyToPostStats
          ? formatCaptureStat(captureStats, captureStats?.filesReviewed)
          : '',
        [columns.captureReviewedTransactions]: includeReadyToPostStats
          ? formatCaptureStat(captureStats, captureStats?.reviewedTransactions)
          : '',
        [columns.capturePostedFiles]: includeReadyToPostStats
          ? formatCaptureStat(captureStats, captureStats?.postedFiles)
          : '',
        [columns.capturePostedThisMonth]: includeReadyToPostStats
          ? formatCaptureStat(captureStats, captureStats?.postedThisMonth)
          : '',
        [columns.captureUploadedTotal]: formatCaptureStat(
          captureStats,
          captureStats?.uploadedFilesTotal
        ),
        [columns.captureUploadedThisMonth]: formatCaptureStat(
          captureStats,
          captureStats?.uploadedThisMonth
        ),
        [columns.captureUploadedManually]: formatCaptureStat(
          captureStats,
          captureStats?.uploadedManually
        ),
        [columns.captureUploadedFromEmail]: formatCaptureStat(
          captureStats,
          captureStats?.uploadedFromEmail
        ),
        [columns.captureUploadedFromGoogleDrive]: formatCaptureStat(
          captureStats,
          captureStats?.uploadedFromGoogleDrive
        ),
        [columns.captureUploadedFromOnedrive]: formatCaptureStat(
          captureStats,
          captureStats?.uploadedFromOneDrive
        ),
        [columns.captureFilesWithErrors]: formatCaptureStat(
          captureStats,
          captureStats?.filesWithErrors
        ),
        [columns.captureDeletedFiles]: formatCaptureStat(
          captureStats,
          captureStats?.deletedFiles
        ),
        [columns.captureActiveFiles]: formatCaptureStat(
          captureStats,
          captureStats?.activeFiles
        ),
        [columns.hmrcConnectionLinked]: formatBoolean(
          Boolean(org.hmrcConnectionUuid),
          t
        ),
        [columns.organisationId]: org.uuid || '',
        [columns.connectionId]: org.connectionId || '',
        [columns.version]: org.version || '',
        [columns.lastUsed]: formatDate(connectionMeta?.lastUsed),
        [columns.lastValidated]: formatDate(connectionMeta?.lastValidated),
        [columns.clientCode]: connectionMeta?.clientCode || '',
        [columns.officeLocation]: connectionMeta?.officeLocation || '',
        [columns.demoCompany]: formatBoolean(connectionMeta?.demoCompany, t),
      };
    });

  const headers = Object.values(columns);
  const ws = XLSX.utils.json_to_sheet(exportRows, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, t('organisation.entities'));
  XLSX.writeFile(wb, `entities-${moment().format('YYYY-MM-DD')}.xlsx`);
}
